// @ts-strict-ignore
import React, { useCallback } from 'react'
import Icon from 'APP/Converse/Icon'
import OcticonsIcon from 'react-native-vector-icons/Octicons'
import EntypoIcon from 'react-native-vector-icons/Entypo'
import I18n from 'APP/I18n'
import {
  Container,
  Distance,
  Title,
  Meta,
  MetaItem,
  Actions,
  StyledAction,
  TextWithSpacing,
  TitleContainer,
} from './style'
import useScreenView from 'APP/Hooks/useScreenView'
import { useTheme } from 'styled-components/native'
import { Pressable, ViewProps } from 'react-native'

export interface SelfNavSearchCardProps extends ViewProps {
  distance?: string
  title: string
  rating?: string | number
  type?: string
  openStatus?: boolean
  closingTime?: string
  address?: string
  onDirections?: () => void
  onCall?: () => void
  onPress?: () => void
  testID?: string
}

const SelfNavSearchCard: React.FC<SelfNavSearchCardProps> = ({
  distance,
  title,
  rating,
  type,
  openStatus,
  closingTime,
  address,
  onDirections,
  onCall,
  onPress,
  ...props
}) => {
  const { isSmallScreen } = useScreenView()
  const buttonWidthVariant = isSmallScreen ? 'flex' : 'fixed'
  const theme = useTheme()
  const callButtonTitle = I18n.t(`SelfNavSearchCard.${onCall ? 'call' : 'noCall'}`)

  const showBody = Boolean(openStatus !== undefined || address)
  const showActions = Boolean(onDirections || onCall)

  const formatTime = (time: string) => {
    return new Date(time).toLocaleTimeString(I18n.locale, {
      hour: 'numeric',
      minute: 'numeric',
    })
  }

  const getClosingTimeText = useCallback(() => {
    if (openStatus === undefined) {
      return I18n.t('SelfNavSearchCard.noOpeningHours')
    }
    if (openStatus === true && closingTime) {
      return I18n.t('SelfNavSearchCard.closingTime', {
        time: formatTime(closingTime),
      })
    }
    return ''
  }, [openStatus, closingTime])

  const SelfNavSearchCardContainer = ({ children }: { children: React.ReactNode }) => {
    if (onPress) {
      return (
        <Pressable
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel={title}
          testID="SELF_NAV_SEARCH_CARD_CONTAINER_BUTTON"
        >
          {children}
        </Pressable>
      )
    }

    return <>{children}</>
  }

  return (
    <SelfNavSearchCardContainer>
      <Container testID="SELF_NAV_SEARCH_CARD" {...props}>
        <>
          <Distance variant="bodySmall">{distance}</Distance>
          <TitleContainer>
            <Title>{title}</Title>
            {onPress && (
              <EntypoIcon
                name="chevron-thin-right"
                size={14}
                color={theme.colors.dim}
                testID="SELF_NAV_SEARCH_CARD_TITLE_CHEVRON"
              />
            )}
          </TitleContainer>
          <Meta>
            <MetaItem>
              {rating && (
                <>
                  <TextWithSpacing mr={2} variant="bodyNormal">
                    {rating}
                  </TextWithSpacing>
                  <Icon src="star" variant="materialIcons" size={16} color="accent" />
                </>
              )}
              {type && rating && (
                <EntypoIcon name="dot-single" size={14} color={theme.colors.text} />
              )}
              {type && (
                <>
                  <TextWithSpacing mr={4} variant="bodyNormal" color="dim">
                    {type}
                  </TextWithSpacing>
                </>
              )}
            </MetaItem>
          </Meta>
        </>
        {showBody && (
          <>
            <Meta>
              <MetaItem>
                <OcticonsIcon name="clock" size={14} color={theme.colors.text} />
                {openStatus !== undefined && (
                  <TextWithSpacing
                    color={openStatus ? 'success' : 'error'}
                    variant="bodySmallBold"
                    ml={8}
                  >
                    {I18n.t(`SelfNavSearchCard.${openStatus ? 'open' : 'closed'}`)}
                  </TextWithSpacing>
                )}
              </MetaItem>
              <MetaItem>
                <TextWithSpacing ml={closingTime ? 4 : 8} variant="bodySmall" color="dim">
                  {getClosingTimeText()}
                </TextWithSpacing>
              </MetaItem>
            </Meta>
            {address && (
              <Meta>
                <MetaItem>
                  <OcticonsIcon name="location" size={14} color={theme.colors.text} />
                  <TextWithSpacing ml={8} variant="bodySmall" color="dim">
                    {address}
                  </TextWithSpacing>
                </MetaItem>
              </Meta>
            )}
          </>
        )}
        {showActions && (
          <Actions>
            {onDirections && (
              <StyledAction
                icon="directions"
                title={I18n.t('SelfNavSearchCard.directions')}
                onPress={onDirections}
                iconVariant="materialIcons"
                widthVariant={buttonWidthVariant}
                accessibilityLabel={I18n.t('SelfNavSearchCard.directions')}
                accessibilityRole="button"
                testID="DIRECTIONS_BUTTON"
              />
            )}
            <StyledAction
              icon={onCall ? 'phone-outline' : undefined}
              title={callButtonTitle}
              onPress={onCall}
              disabled={!onCall}
              iconVariant="material"
              widthVariant={buttonWidthVariant}
              accessibilityLabel={callButtonTitle}
              accessibilityRole="button"
              testID="CALL_BUTTON"
            />
          </Actions>
        )}
      </Container>
    </SelfNavSearchCardContainer>
  )
}

export default SelfNavSearchCard
